import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';
import './Sessions.css';

const Sessions = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pdvs, setPdvs] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);

  const [formData, setFormData] = useState({
    pdv_id: '',
    name: '',
    start_time: '',
    end_time: '',
    gain_mode: 'auto'
  });

  useEffect(() => {
    loadSessions();
    loadPdvs();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/sessions', {
        params: { brand: BRAND }
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des sessions' });
    } finally {
      setLoading(false);
    }
  };

  const loadPdvs = async () => {
    try {
      const response = await api.get('/admin/pdvs', { params: { brand: BRAND } });
      setPdvs(response.data);
    } catch (error) {
      console.error('Error loading PDVs:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': { color: '#228b22', bg: '#e8f5e8', label: 'Active' },
      'inactive': { color: '#999', bg: '#f5f5f5', label: 'Inactive' },
      'completed': { color: '#1565c0', bg: '#e3f2fd', label: 'Terminée' }
    };
    return statusMap[status] || { color: '#666', bg: '#f0f0f0', label: status };
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.post('/admin/sessions', {
        ...formData,
        pdv_id: parseInt(formData.pdv_id),
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
        brand: BRAND
      });

      setMessage({ type: 'success', text: 'Session créée avec succès !' });
      setShowCreateModal(false);
      setFormData({
        pdv_id: '',
        name: '',
        start_time: '',
        end_time: '',
        gain_mode: 'auto'
      });
      loadSessions();
    } catch (error) {
      console.error('Error creating session:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Erreur lors de la création de la session'
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="sessions-container">
        <div className="loading-screen">
          <div className="spinner-border" role="status" style={{ color: '#1565c0' }}></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sessions-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo-circle">
            <img src="/images/logo-supermont.png" alt="Supermont" style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }} />
          </div>
          <h3>Les Perles de Vie</h3>
          <p className="sidebar-subtitle">Supermont</p>
        </div>

        <nav className="sidebar-nav">
          <button className="nav-item" onClick={() => navigate('/dashboard')}>
            <i className="fas fa-arrow-left"></i>
            <span>Retour Dashboard</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <i className="fas fa-user-circle"></i>
            <span>{user?.username}</span>
          </div>
          <button onClick={handleLogout} className="logout-btn">
            <i className="fas fa-sign-out-alt"></i>
            Déconnexion
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <header className="content-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button className="btn-back" onClick={handleBack}>
              <i className="fas fa-arrow-left"></i>
              Retour
            </button>
            <h1>Sessions</h1>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button className="btn-create" onClick={() => setShowCreateModal(true)}>
              <i className="fas fa-plus"></i>
              Nouvelle Session
            </button>
            <p className="brand-badge">Supermont</p>
          </div>
        </header>

        {message.text && (
          <div className={`alert alert-${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="sessions-grid">
          {sessions.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-calendar-alt" style={{ fontSize: '64px', color: '#1565c0', marginBottom: '20px' }}></i>
              <h3>Aucune session</h3>
              <p>Il n'y a aucune session pour le moment.</p>
            </div>
          ) : (
            sessions.map((session) => {
              const statusBadge = getStatusBadge(session.status);
              return (
                <div key={session.id} className="session-card">
                  <div className="card-header">
                    <div className="card-icon">
                      <i className="fas fa-calendar-alt" style={{ color: '#fff' }}></i>
                    </div>
                    <h2>{session.name || `Session ${session.id}`}</h2>
                  </div>
                  <div className="card-body">
                    <div className="session-info">
                      <div className="info-row">
                        <span className="info-label">Date :</span>
                        <span className="info-value">{new Date(session.date).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Point de vente :</span>
                        <span className="info-value">{session.pdv_name || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Ville :</span>
                        <span className="info-value">{session.city_name || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Statut :</span>
                        <span className="status-badge" style={{
                          background: statusBadge.bg,
                          color: statusBadge.color
                        }}>
                          {statusBadge.label}
                        </span>
                      </div>
                      {session.total_participants !== undefined && (
                        <div className="info-row">
                          <span className="info-label">Participants :</span>
                          <span className="info-value">{session.total_participants}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Modal de création de session */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouvelle Session</h3>
              <button onClick={() => setShowCreateModal(false)} className="close-btn">×</button>
            </div>
            <form onSubmit={handleCreateSession} className="modal-form">
              <div className="form-group">
                <label>Point de vente *</label>
                <select
                  name="pdv_id"
                  value={formData.pdv_id}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Sélectionner un point de vente</option>
                  {pdvs.map(pdv => (
                    <option key={pdv.id} value={pdv.id}>
                      {pdv.name} ({pdv.city_name})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Nom de la session</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ex: Session Douala - Akwa"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date/Heure début *</label>
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Date/Heure fin *</label>
                  <input
                    type="datetime-local"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Mode de gain</label>
                <select
                  name="gain_mode"
                  value={formData.gain_mode}
                  onChange={handleInputChange}
                >
                  <option value="auto">Automatique</option>
                  <option value="ratio">Ratio (1 gagnant tous les X lancers)</option>
                  <option value="pourcentage">Pourcentage global</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-cancel">
                  Annuler
                </button>
                <button type="submit" className="btn-submit" disabled={createLoading}>
                  {createLoading ? 'Création...' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sessions;
